/**
 * Recovers a tab whose lazy route chunk no longer exists on the CDN.
 *
 * Every route in `app.routes.ts` is a `loadComponent`, so advancing a step
 * fetches a content-hashed chunk at that moment. A deploy replaces those
 * filenames, and CloudFront maps S3's 403 for a missing key to `/index.html`
 * with **HTTP 200 and `content-type: text/html`** — so the browser does not even
 * see a 404, it parses HTML as JavaScript and the booking dies on a blank
 * screen. Observed in production as
 * `TypeError: Failed to fetch dynamically imported module: <url>`.
 *
 * The deploy workflow now leaves the previous build's chunks in place, which
 * closes the window for tabs opened shortly before a deploy. This is the second
 * half: a tab open across *two* deploys, or one whose chunks the bucket
 * lifecycle rule has since expired, still has to end up somewhere other than a
 * dead screen. Reloading picks up the current `index.html` and its chunk names.
 */

/** Timestamp of the last recovery reload, so a broken deploy cannot put the tab
 *  in a reload loop. `sessionStorage`, not memory: the reload is the whole point,
 *  so the guard has to survive it. Per-tab, so one wedged tab cannot silence
 *  recovery in another. */
const RELOAD_MARKER_KEY = 'funnel.chunkReloadAt';

/** How long a reload counts as "we already tried this". Long enough that a
 *  reload which lands on the same broken build gives up instead of looping,
 *  short enough that a tab left open across a later deploy gets its own chance. */
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Whether the error is a lazy chunk that could not be fetched or parsed.
 *
 * Matched on the message because engines disagree on the wording and none of
 * them use a distinguishable error type — all three are a plain `TypeError`.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    // Chromium
    message.includes('Failed to fetch dynamically imported module') ||
    // Firefox
    message.includes('error loading dynamically imported module') ||
    // Safari
    message.includes('Importing a module script failed')
  );
}

/**
 * The don't-loop rule: reload only if this tab has not already reloaded for a
 * chunk failure in the last {@link RELOAD_COOLDOWN_MS}.
 *
 * Kept separate from the browser work so it can be tested — getting this wrong
 * costs a customer an infinite reload loop, which is worse than the dead screen
 * it is trying to fix.
 */
export function shouldReloadAfterChunkFailure(marker: string | null, now: number): boolean {
  if (marker === null) return true;
  const lastReloadAt = Number(marker);
  // Unparseable (someone else's key, a truncated write) — treat it as "already
  // tried". Failing closed here can only cost one error report; failing open
  // could cost a reload loop.
  if (!Number.isFinite(lastReloadAt)) return false;
  return now - lastReloadAt > RELOAD_COOLDOWN_MS;
}

/**
 * Reloads once for a chunk failure. Returns whether a reload was started, so a
 * caller can tell "handled" from "give up and let the error surface".
 *
 * On the give-up path nothing is suppressed: the navigation error keeps
 * propagating to the ErrorHandler and so still reaches Sentry, which is how a
 * genuinely broken deploy becomes visible instead of silently reloading forever.
 */
export function recoverFromChunkLoadFailure(
  error: unknown,
  /** Seam for tests — jsdom has no navigation. */
  reload: () => void = () => window.location.reload(),
): boolean {
  if (!isChunkLoadError(error)) return false;

  const now = Date.now();
  if (!shouldReloadAfterChunkFailure(readMarker(), now)) return false;

  writeMarker(now);
  reload();
  return true;
}

function readMarker(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch {
    // Blocked storage (private mode, cookies off). Without a guard a reload
    // could loop, so report the failure rather than risk it.
    return String(Date.now());
  }
}

function writeMarker(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(now));
  } catch {
    // Unreachable in practice — `readMarker` already returned a "don't reload"
    // marker if storage throws — but a write that silently fails must not
    // prevent the reload it was guarding.
  }
}
